import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  Bell, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Send, 
  Eye, 
  Clock,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  AlertOctagon,
  Megaphone,
  ClipboardList,
  TrendingUp,
  Mail,
  Users,
  Calendar
} from 'lucide-react';
import { 
  getAllNotifications, 
  createNotification, 
  updateNotification, 
  deleteNotification,
  getCompanyList,
  calculateRecipients,
  sendNotificationEmails,
  getNotificationStats
} from '../../services/notificationService';
import { showNotification, showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';

const AdminCustomerNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [estimatedRecipients, setEstimatedRecipients] = useState(0);
  const [previewMode, setPreviewMode] = useState(false);
  const [stats, setStats] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    target_type: 'all_users',
    target_companies: [],
    email_enabled: true,
    scheduled_send_at: null,
    expires_at: null,
    is_active: true
  });

  const notificationTypes = [
    { value: 'info', label: 'Info', color: 'blue', icon: Bell, iconColor: 'text-blue-600' },
    { value: 'warning', label: 'Warning', color: 'yellow', icon: AlertTriangle, iconColor: 'text-orange-500' },
    { value: 'urgent', label: 'Urgent', color: 'red', icon: AlertOctagon, iconColor: 'text-red-600' },
    { value: 'announcement', label: 'Announcement', color: 'purple', icon: Megaphone, iconColor: 'text-purple-600' }
  ];

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ]
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (formData.target_type && (formData.target_type === 'all_users' || formData.target_companies.length > 0)) {
      updateRecipientCount();
    }
  }, [formData.target_type, formData.target_companies]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [notificationsData, companiesData] = await Promise.all([
        getAllNotifications(),
        getCompanyList()
      ]);
      setNotifications(notificationsData);
      setCompanies(companiesData);
      
      // Fetch stats for each notification
      const statsPromises = notificationsData.map(n => getNotificationStats(n.id));
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      notificationsData.forEach((n, index) => {
        statsMap[n.id] = statsResults[index];
      });
      setStats(statsMap);
    } catch (error) {
      showError(error, 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const updateRecipientCount = async () => {
    try {
      const count = await calculateRecipients(formData.target_type, formData.target_companies);
      setEstimatedRecipients(count);
    } catch (error) {
      console.error('Error calculating recipients:', error);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCompanyToggle = (companyName) => {
    setFormData(prev => {
      const current = prev.target_companies || [];
      const updated = current.includes(companyName)
        ? current.filter(c => c !== companyName)
        : [...current, companyName];
      return {
        ...prev,
        target_companies: updated
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      showError(new Error('Title is required'), 'Title is required');
      return;
    }
    
    if (!formData.message.trim()) {
      showError(new Error('Message is required'), 'Message is required');
      return;
    }

    if (formData.target_type === 'specific_companies' && formData.target_companies.length === 0) {
      showError(new Error('Select at least one company'), 'Select at least one company for specific targeting');
      return;
    }

    try {
      setSaving(true);
      
      const notificationData = {
        title: formData.title.trim(),
        message: formData.message,
        type: formData.type,
        target_type: formData.target_type,
        target_companies: formData.target_type === 'specific_companies' ? formData.target_companies : null,
        email_enabled: formData.email_enabled,
        scheduled_send_at: formData.scheduled_send_at || null,
        expires_at: formData.expires_at || null,
        is_active: formData.is_active
      };
      
      if (editingId) {
        await updateNotification(editingId, notificationData);
        showNotification('Notification updated successfully!', 'success');
      } else {
        await createNotification(notificationData);
        showNotification('Notification created successfully!', 'success');
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      showError(error, 'Failed to save notification');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (notification) => {
    setFormData({
      title: notification.title,
      message: notification.message,
      type: notification.type,
      target_type: notification.target_type,
      target_companies: notification.target_companies || [],
      email_enabled: notification.email_enabled,
      scheduled_send_at: notification.scheduled_send_at,
      expires_at: notification.expires_at,
      is_active: notification.is_active
    });
    setEditingId(notification.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await deleteNotification(id);
      showNotification('Notification deleted successfully', 'success');
      fetchData();
    } catch (error) {
      showError(error, 'Failed to delete notification');
    }
  };

  const handleToggleActive = async (notification) => {
    try {
      await updateNotification(notification.id, { is_active: !notification.is_active });
      showNotification(
        `Notification ${!notification.is_active ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
      fetchData();
    } catch (error) {
      showError(error, 'Failed to toggle notification status');
    }
  };

  const handleSendEmails = async (notificationId) => {
    if (!window.confirm('Are you sure you want to send email notifications? This cannot be undone.')) {
      return;
    }

    try {
      setSendingEmail(true);
      const result = await sendNotificationEmails(notificationId);
      
      if (result.success) {
        showNotification(
          `Emails sent successfully! ${result.data.success} sent, ${result.data.failed} failed`,
          'success'
        );
        fetchData();
      } else {
        throw new Error(result.error || 'Failed to send emails');
      }
    } catch (error) {
      showError(error, 'Failed to send emails');
    } finally {
      setSendingEmail(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'info',
      target_type: 'all_users',
      target_companies: [],
      email_enabled: true,
      scheduled_send_at: null,
      expires_at: null,
      is_active: true
    });
    setEditingId(null);
    setShowForm(false);
    setPreviewMode(false);
    setEstimatedRecipients(0);
  };

  const getTypeColor = (type) => {
    const typeInfo = notificationTypes.find(t => t.value === type);
    return typeInfo ? typeInfo.color : 'gray';
  };

  const getTypeIcon = (type, sizeClass = 'h-4 w-4') => {
    const typeInfo = notificationTypes.find(t => t.value === type);
    const Icon = typeInfo ? typeInfo.icon : ClipboardList;
    const iconColor = typeInfo ? typeInfo.iconColor : 'text-gray-500';
    return <Icon className={`${sizeClass} ${iconColor} flex-shrink-0`} aria-hidden="true" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Customer Notifications"
        description="Create and manage notifications for your customers"
        actions={!showForm && (
          <Button
            variant="primary"
            onClick={() => setShowForm(true)}
            icon={<Plus className="w-4 h-4" />}
          >
            Create Notification
          </Button>
        )}
      />

      {/* Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-semibold text-gray-900">
              {editingId ? 'Edit Notification' : 'Create New Notification'}
            </h3>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setPreviewMode(!previewMode)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <Eye className="w-4 h-4 mr-1" />
                {previewMode ? 'Edit' : 'Preview'}
              </button>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {!previewMode ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title and Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter notification title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {notificationTypes.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => handleFormChange('type', type.value)}
                        className={`px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
                          formData.type === type.value
                            ? `bg-${type.color}-100 border-${type.color}-500 text-${type.color}-700`
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="inline-flex items-center gap-1">
                          <type.icon className={`h-4 w-4 ${type.iconColor}`} aria-hidden="true" />
                          {type.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Message Editor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <div className="border border-gray-300 rounded-md overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={formData.message}
                    onChange={(value) => handleFormChange('message', value)}
                    modules={quillModules}
                    className="bg-white"
                    style={{ minHeight: '200px' }}
                  />
                </div>
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.target_type === 'all_users'}
                      onChange={() => handleFormChange('target_type', 'all_users')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">All Users</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={formData.target_type === 'specific_companies'}
                      onChange={() => handleFormChange('target_type', 'specific_companies')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Specific Companies</span>
                  </label>

                  {formData.target_type === 'specific_companies' && (
                    <div className="ml-6 mt-2 space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
                      {companies.map(company => (
                        <label key={company.company_name} className="flex items-center hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={formData.target_companies.includes(company.company_name)}
                            onChange={() => handleCompanyToggle(company.company_name)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {company.company_name} 
                            <span className="text-gray-500 ml-1">({company.user_count} users)</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center text-sm text-blue-600 bg-blue-50 p-2 rounded">
                    <Users className="w-4 h-4 mr-2" />
                    Estimated Recipients: {estimatedRecipients} users
                  </div>
                </div>
              </div>

              {/* Email Settings */}
              <div className="border-t border-gray-200 pt-4">
                <label className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    checked={formData.email_enabled}
                    onChange={(e) => handleFormChange('email_enabled', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Send email notification to users
                  </span>
                </label>

                {formData.email_enabled && (
                  <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Schedule Send (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.scheduled_send_at || ''}
                        onChange={(e) => handleFormChange('scheduled_send_at', e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Leave empty to send immediately after publishing
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Expiry Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Expiry Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.expires_at || ''}
                  onChange={(e) => handleFormChange('expires_at', e.target.value)}
                  className="block w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Notification will automatically hide after this date
                </p>
              </div>

              {/* Active Status */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Publish notification (make visible to users)
                  </span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Save className="w-4 h-4 mr-2" />
                      {editingId ? 'Update' : 'Create'} Notification
                    </div>
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Preview Mode */
            <div className="space-y-4">
              <div className="bg-gray-50 border-l-4 border-blue-500 p-4 rounded">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
                    {getTypeIcon(formData.type, 'h-5 w-5')} {formData.title}
                  </h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium bg-${getTypeColor(formData.type)}-100 text-${getTypeColor(formData.type)}-700`}>
                    {formData.type}
                  </span>
                </div>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: formData.message }}
                />
              </div>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Target:</strong> {formData.target_type === 'all_users' ? 'All Users' : `${formData.target_companies.length} Companies`}</p>
                <p><strong>Recipients:</strong> {estimatedRecipients} users</p>
                <p><strong>Email:</strong> {formData.email_enabled ? 'Enabled' : 'Disabled'}</p>
                {formData.expires_at && <p><strong>Expires:</strong> {new Date(formData.expires_at).toLocaleString()}</p>}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            All Notifications ({notifications.length})
          </h3>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-600">Loading notifications...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No notifications created yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.map((notification) => {
                const notifStats = stats[notification.id] || {};
                return (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="flex-shrink-0">{getTypeIcon(notification.type, 'h-6 w-6')}</span>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </h4>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${getTypeColor(notification.type)}-100 text-${getTypeColor(notification.type)}-800`}>
                                {notification.type}
                              </span>
                              {notification.is_active ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Inactive
                                </span>
                              )}
                              {notification.email_sent && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <Mail className="w-3 h-3 mr-1" />
                                  Email Sent
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="ml-11 text-sm text-gray-600 space-y-1">
                          <p>
                            <strong>Target:</strong> {notification.target_type === 'all_users' ? 'All Users' : `${notification.target_companies?.length || 0} Companies`}
                          </p>
                          {notifStats.total_recipients && (
                            <div className="flex items-center space-x-4">
                              <span><Users className="w-4 h-4 inline mr-1" />{notifStats.total_recipients} recipients</span>
                              <span><Eye className="w-4 h-4 inline mr-1" />{notifStats.dismissals} dismissed</span>
                              {notifStats.emails_sent > 0 && (
                                <span><Mail className="w-4 h-4 inline mr-1" />{notifStats.emails_sent} emails sent</span>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-gray-500">
                            Created {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleToggleActive(notification)}
                          className={`p-2 rounded ${notification.is_active ? 'text-gray-600 hover:text-gray-900' : 'text-green-600 hover:text-green-900'}`}
                          title={notification.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {notification.is_active ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </button>

                        {notification.email_enabled && !notification.email_sent && (
                          <button
                            onClick={() => handleSendEmails(notification.id)}
                            disabled={sendingEmail}
                            className="p-2 text-blue-600 hover:text-blue-900 disabled:opacity-50"
                            title="Send Emails"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}

                        <button
                          onClick={() => handleEdit(notification)}
                          className="p-2 text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleDelete(notification.id)}
                          className="p-2 text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCustomerNotifications;
